/**
 * video-to-sound/plan.js — window.VideoSoundPlan
 *
 * The page's copy of App\Services\VideoSound\VideoSoundPlan: which pieces of
 * the range each engine gets, how many vendor calls that is, and what it
 * costs. The server bills by its own copy; this one exists so the price under
 * the button is the price that will be charged, before anything is sent.
 * Both are held to tests/fixtures/video-sound-plan-cases.json (PHP:
 * VideoSoundPriceTest, node: tests/js/video-sound-plan.test.mjs) — change one,
 * the other test fails.
 *
 *   VideoSoundPlan.make(shape, engine, total, scenes, variants, music)
 *     → { total, variants, scenes, pieces, calls, units, tokens }
 */
(function (root) {
    'use strict';

    var MIN_SCENE_S = 1.0;

    function round3(v) { return Math.round(v * 1000) / 1000; }
    // Mirror of VideoSoundPlan::billSeconds — up to 50 ms past a whole second
    // is not billed (camera clips run 8.008 s; fal bills fractional seconds).
    var BILL_GRACE_S = 0.05;
    function billSeconds(len) { return Math.max(1, Math.ceil(round3(len) - BILL_GRACE_S)); }

    function variants(shape, engine, asked) {
        var allowed = ((shape.engines[engine] || {}).variants || [1]).slice().sort(function (a, b) { return a - b; });
        if (allowed.indexOf(asked) >= 0) return asked;
        var pick = allowed[0];
        for (var i = 0; i < allowed.length; i++) if (allowed[i] <= asked) pick = allowed[i];
        return pick;
    }

    function normaliseScenes(scenes, total) {
        if (!(total > 0)) return [];
        var cuts = [];
        (scenes || []).forEach(function (s) {
            var st = +(s && s.start) || 0;
            if (st > 0 && st < total) {
                var r = round3(st);
                if (cuts.indexOf(r) < 0) cuts.push(r);
            }
        });
        cuts.sort(function (a, b) { return a - b; });
        var bounds = [0].concat(cuts, [total]);
        var out = [];
        for (var i = 0; i < bounds.length - 1; i++) {
            var a = bounds[i], b = bounds[i + 1];
            if (b - a < MIN_SCENE_S && out.length) { out[out.length - 1].end = b; continue; }
            out.push({ start: a, end: b });
        }
        if (out.length > 1 && out[0].end - out[0].start < MIN_SCENE_S) {
            out[1].start = 0;
            out.shift();
        }
        return out;
    }

    function pieces(scenes, max, overlap) {
        var out = [];
        scenes.forEach(function (s, si) {
            var a = s.start, b = s.end, len = b - a;
            if (max === null || max === undefined || len <= max) {
                out.push({ scene: si, start: a, end: b, xfade_in: 0 });
                return;
            }
            var ov = Math.min(overlap, max / 4);
            var k = Math.ceil((len - ov) / (max - ov));
            var L = (len + (k - 1) * ov) / k;
            for (var i = 0; i < k; i++) {
                var st = a + i * (L - ov);
                var en = i === k - 1 ? b : st + L;
                out.push({ scene: si, start: round3(st), end: round3(en), xfade_in: i === 0 ? 0 : round3(ov) });
            }
        });
        return out;
    }

    function make(shape, engine, total, scenes, nVariants, music) {
        var e = shape.engines[engine];
        if (!e) throw new Error('unknown engine ' + engine);
        total = round3(Math.max(0, +total || 0));
        var v = variants(shape, engine, nVariants | 0);
        var sc = normaliseScenes(scenes, total);
        var pc = pieces(sc, e.max_piece_s, +shape.overlap_s || 1);
        var tpu = Math.max(1, e.tokens_per_unit | 0);
        var calls, units;
        if (e.unit === 'call') {
            calls = pc.length * v;
            units = calls;
        } else if (e.unit === 'second_sample') {
            var spc = Math.max(1, e.samples_per_call | 0);
            calls = pc.length * Math.ceil(v / spc);
            units = 0;
            pc.forEach(function (p) { units += billSeconds(p.end - p.start) * v; });
        } else {
            calls = v;
            units = billSeconds(total) * v;
        }
        var tokens = Math.floor(units * tpu);
        if (music && shape.music && shape.music.enabled) {
            tokens += shape.music.tokens | 0;
            calls += 1;
        }
        return { engine: engine, total: total, variants: v, music: !!music, scenes: sc, pieces: pc,
                 calls: calls, units: units, tokens: Math.max(1, tokens) };
    }

    var api = { make: make, variants: variants, normaliseScenes: normaliseScenes, pieces: pieces, MIN_SCENE_S: MIN_SCENE_S };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.VideoSoundPlan = api;
})(typeof window !== 'undefined' ? window : this);
